import {
  Avatar,
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  HStack,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Radio,
  RadioGroup,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  VStack,
} from '@chakra-ui/react';
import React, { useCallback, useState } from 'react';
import Select from 'react-select';
import {
  CommentLogEntry,
  IntentType,
  normalizeAppointmentTypeOutcome,
  normalizePracticeProviderLevel,
  normalizeSpecialtyOutcome,
  TransferOutcome,
  TransferOutcomesData,
} from '../../types/transferOutcomes';
import {
  getAppointmentTypeDisplayName,
  getAppointmentTypeOptions,
} from '../../utils/appointmentTypes';
import FormattedText from '../rich-text-editor/FormattedText';
import RichTextEditor from '../rich-text-editor/RichTextEditor';

interface TransferOutcomesConfigProps {
  isPracticeLevel: boolean;
  transferOutcomes: TransferOutcomesData;
  comment?: string; // Current comment being entered
  commentLog?: CommentLogEntry[]; // Audit log of all comments and actions
  onTransferOutcomesChange: (data: TransferOutcomesData) => void;
  onCommentChange: (comment: string) => void;
  onAddComment?: () => void; // Handler for adding comment without closing modal
  isAddingComment?: boolean; // Loading state for adding comment
  showSubmitForApproval?: boolean;
  showApproveButton?: boolean;
  hasExistingId?: boolean; // Whether the alert/note has been saved (has an ID)
  canApprove?: boolean; // Whether approval is currently permitted
  approveDisabledReason?: string;
  onSubmitForApproval?: () => void;
  onApprove?: () => void;
  isSubmitting?: boolean;
  isApproving?: boolean;
  specialtyOptions?: Array<{ label: string; value: string }>;
}

const TransferOutcomesConfig: React.FC<
  TransferOutcomesConfigProps
> = ({
  isPracticeLevel,
  transferOutcomes,
  comment = '',
  commentLog = [],
  onTransferOutcomesChange,
  onCommentChange,
  onAddComment,
  isAddingComment = false,
  showSubmitForApproval = false,
  showApproveButton = false,
  hasExistingId = false, // Whether alert/note has been saved (has an ID)
  canApprove = true,
  approveDisabledReason,
  onSubmitForApproval,
  onApprove,
  isSubmitting = false,
  isApproving = false,
  specialtyOptions = [],
}) => {
  const [selectedAppointmentType, setSelectedAppointmentType] =
    useState<{ label: string; value: string } | null>(null);
  const [selectedSpecialty, setSelectedSpecialty] = useState<{
    label: string;
    value: string;
  } | null>(null);

  // Note: Default appointment types should be initialized by parent component
  // using createDefaultTransferOutcomes() helper function

  const handlePracticeProviderLevelChange = useCallback(
    (intent: IntentType, value: TransferOutcome) => {
      const current = normalizePracticeProviderLevel(
        transferOutcomes.practiceProviderLevel
      );
      onTransferOutcomesChange({
        ...transferOutcomes,
        practiceProviderLevel: {
          ...current,
          [intent]: value,
        },
      });
    },
    [transferOutcomes, onTransferOutcomesChange]
  );

  const handleAppointmentTypeChange = useCallback(
    (
      appointmentType: string,
      intent: IntentType,
      value: TransferOutcome
    ) => {
      const currentAppointmentTypes =
        transferOutcomes.appointmentTypes || {};
      const currentOutcome = normalizeAppointmentTypeOutcome(
        currentAppointmentTypes[appointmentType]
      );
      onTransferOutcomesChange({
        ...transferOutcomes,
        appointmentTypes: {
          ...currentAppointmentTypes,
          [appointmentType]: {
            ...currentOutcome,
            [intent]: value,
          },
        },
      });
    },
    [transferOutcomes, onTransferOutcomesChange]
  );

  const handleAddAppointmentType = useCallback(() => {
    if (!selectedAppointmentType) return;

    const appointmentTypeValue = selectedAppointmentType.value;
    const currentAppointmentTypes =
      transferOutcomes.appointmentTypes || {};
    if (currentAppointmentTypes[appointmentTypeValue]) {
      // Already exists
      return;
    }

    onTransferOutcomesChange({
      ...transferOutcomes,
      appointmentTypes: {
        ...currentAppointmentTypes,
        [appointmentTypeValue]: {
          reschedule: 'transfer',
          schedule: 'transfer',
          confirm: 'not-transfer',
          cancel: 'not-transfer',
        },
      },
    });

    setSelectedAppointmentType(null);
  }, [
    selectedAppointmentType,
    transferOutcomes,
    onTransferOutcomesChange,
  ]);

  const handleRemoveAppointmentType = useCallback(
    (appointmentType: string) => {
      const currentAppointmentTypes = {
        ...(transferOutcomes.appointmentTypes || {}),
      };
      delete currentAppointmentTypes[appointmentType];
      onTransferOutcomesChange({
        ...transferOutcomes,
        appointmentTypes: currentAppointmentTypes,
      });
    },
    [transferOutcomes, onTransferOutcomesChange]
  );

  const handleSpecialtyChange = useCallback(
    (
      specialty: string,
      intent: IntentType,
      value: TransferOutcome
    ) => {
      const currentSpecialties = transferOutcomes.specialties || {};
      const currentOutcome = normalizeSpecialtyOutcome(
        currentSpecialties[specialty]
      );
      onTransferOutcomesChange({
        ...transferOutcomes,
        specialties: {
          ...currentSpecialties,
          [specialty]: {
            ...currentOutcome,
            [intent]: value,
          },
        },
      });
    },
    [transferOutcomes, onTransferOutcomesChange]
  );

  const handleAddSpecialty = useCallback(() => {
    if (!selectedSpecialty) return;

    const specialtyValue = selectedSpecialty.value;
    const currentSpecialties = transferOutcomes.specialties || {};
    if (currentSpecialties[specialtyValue]) {
      // Already exists
      return;
    }

    onTransferOutcomesChange({
      ...transferOutcomes,
      specialties: {
        ...currentSpecialties,
        [specialtyValue]: {
          schedule: 'transfer',
          reschedule: 'transfer',
          confirm: 'not-transfer',
          cancel: 'not-transfer',
        },
      },
    });

    setSelectedSpecialty(null);
  }, [selectedSpecialty, transferOutcomes, onTransferOutcomesChange]);

  const handleRemoveSpecialty = useCallback(
    (specialty: string) => {
      const currentSpecialties = { ...transferOutcomes.specialties };
      delete currentSpecialties[specialty];
      onTransferOutcomesChange({
        ...transferOutcomes,
        specialties: currentSpecialties,
      });
    },
    [transferOutcomes, onTransferOutcomesChange]
  );

  const appointmentTypeOptions = getAppointmentTypeOptions();

  // Get all configured appointment types (default + custom)
  const configuredAppointmentTypes = Object.keys(
    transferOutcomes.appointmentTypes || {}
  );

  // Get configured specialties
  const configuredSpecialties = Object.keys(
    transferOutcomes.specialties || {}
  );

  // Filter out already configured appointment types from dropdown
  const availableAppointmentTypes = appointmentTypeOptions.filter(
    (option) => !configuredAppointmentTypes.includes(option.value)
  );

  // Filter out already configured specialties from dropdown
  const availableSpecialties = specialtyOptions.filter(
    (option) => !configuredSpecialties.includes(option.value)
  );

  // Can submit if comment is provided AND alert/note has been saved (has an ID)
  const canSubmitForApproval =
    comment.trim().length > 0 && hasExistingId;

  // Helper messages for disabled states
  const getSubmitForApprovalTooltip = (): string => {
    if (!hasExistingId) {
      return 'Please save the alert/note first before submitting for approval';
    }
    if (!comment.trim()) {
      return 'Please enter a comment before submitting for approval';
    }
    return 'Submit transfer outcomes for CHS approval';
  };

  const getAddCommentTooltip = (): string => {
    if (!hasExistingId) {
      return 'Please save the alert/note first before adding comments';
    }
    if (!comment.trim()) {
      return 'Please enter a comment before adding it';
    }
    return 'Add this comment to the audit log';
  };

  // Format timestamp for display
  const formatTimestamp = (timestamp: string): string => {
    try {
      const date = new Date(timestamp);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });
    } catch {
      return timestamp;
    }
  };

  // Get action display text
  const getActionText = (
    action: CommentLogEntry['action']
  ): string => {
    switch (action) {
      case 'created':
        return 'Created';
      case 'submitted_for_approval':
        return 'Submitted for Approval';
      case 'updated':
        return 'Updated';
      case 'approved':
        return 'Approved';
      case 'comment_added':
        return 'Comment Added';
      default:
        return action;
    }
  };

  // Get action badge color
  const getActionBadgeColor = (
    action: CommentLogEntry['action']
  ): string => {
    switch (action) {
      case 'created':
        return 'green';
      case 'submitted_for_approval':
        return 'orange';
      case 'updated':
        return 'blue';
      case 'approved':
        return 'purple';
      case 'comment_added':
        return 'teal';
      default:
        return 'gray';
    }
  };

  // Get user initials for avatar
  const getUserInitials = (name: string): string => {
    if (!name) return '??';
    const names = name.split(' ');
    if (names.length >= 2) {
      return `${names[0][0]}${
        names[names.length - 1][0]
      }`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // Get initials from entry - prioritize userName, then userId
  const getEntryInitials = (entry: CommentLogEntry): string => {
    // Always use userName if available for better avatar generation
    if (entry.userName && entry.userName.trim()) {
      return getUserInitials(entry.userName);
    }
    // Fallback to userId if no name - extract meaningful part from userId
    // userId format: "google-apps|dbhumra@denimhealth.com" or similar
    if (entry.userId && entry.userId.trim()) {
      // Try to extract email part from userId if it contains @
      if (entry.userId.includes('@')) {
        const emailPart = entry.userId.split('@')[0];
        // Extract the part after | if present (e.g., "dbhumra" from "google-apps|dbhumra@denimhealth.com")
        const namePart = emailPart.includes('|')
          ? emailPart.split('|')[1]
          : emailPart;
        return getUserInitials(namePart);
      }
      // If no @, try to extract part after | if present
      if (entry.userId.includes('|')) {
        const namePart = entry.userId.split('|')[1];
        return getUserInitials(namePart);
      }
      // Otherwise use first 2 characters of userId
      return getUserInitials(entry.userId);
    }
    // Last resort: return default
    return '??';
  };

  // Get display name for user
  const getUserDisplayName = (entry: CommentLogEntry): string => {
    return entry.userName || entry.userEmail || entry.userId;
  };

  return (
    <VStack spacing="1.5rem" align="stretch">
      {/* Divider */}
      <Box borderTop="1px solid" borderColor="gray.200" pt="1rem" />

      {/* Section Header */}
      <Box>
        <Text fontSize="1rem" fontWeight="600" color="gray.800">
          {isPracticeLevel
            ? 'Practice level T/NT for PAT & Scheduling'
            : 'Provider level T/NT for PAT & Scheduling'}
        </Text>
      </Box>

      {/* Practice/Provider Level Outcome */}
      <Box>
        <Text
          mb="0.5rem"
          fontSize="0.875rem"
          fontWeight="500"
          color="gray.700"
        >
          Outcome for {isPracticeLevel ? 'PRACTICE:' : 'PROVIDER:'}
        </Text>
        <Box
          border="1px solid"
          borderColor="gray.200"
          borderRadius="md"
          overflow="visible"
        >
          <TableContainer>
            <Table variant="simple" size="sm">
              <Thead bg="gray.50">
                <Tr>
                  <Th
                    fontSize="0.875rem"
                    fontWeight="600"
                    color="gray.800"
                  >
                    Intent
                  </Th>
                  <Th
                    fontSize="0.875rem"
                    fontWeight="600"
                    color="gray.800"
                    borderLeft="1px solid"
                    borderColor="gray.200"
                  >
                    Outcome
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                {(
                  [
                    'schedule',
                    'reschedule',
                    'confirm',
                    'cancel',
                  ] as IntentType[]
                ).map((intent) => {
                  const normalized = normalizePracticeProviderLevel(
                    transferOutcomes.practiceProviderLevel
                  );
                  return (
                    <Tr
                      key={intent}
                      borderBottom="1px solid"
                      borderColor="gray.200"
                    >
                      <Td
                        fontSize="0.875rem"
                        color="gray.800"
                        textTransform="capitalize"
                      >
                        {intent}
                      </Td>
                      <Td
                        borderLeft="1px solid"
                        borderColor="gray.200"
                      >
                        <RadioGroup
                          value={normalized[intent]}
                          onChange={(value) =>
                            handlePracticeProviderLevelChange(
                              intent,
                              value as TransferOutcome
                            )
                          }
                        >
                          <HStack spacing={4}>
                            <Radio value="transfer" size="sm">
                              <Text fontSize="0.875rem">
                                Transfer
                              </Text>
                            </Radio>
                            <Radio value="not-transfer" size="sm">
                              <Text fontSize="0.875rem">
                                Not Transfer
                              </Text>
                            </Radio>
                          </HStack>
                        </RadioGroup>
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </TableContainer>
        </Box>
      </Box>

      {/* Specialty Outcomes - Practice Level Only */}
      {isPracticeLevel && (
        <Box>
          <Text
            mb="0.5rem"
            fontSize="0.875rem"
            fontWeight="500"
            color="gray.700"
          >
            Outcome for Specialty:
          </Text>
          <Box
            border="1px solid"
            borderColor="gray.200"
            borderRadius="md"
            overflow="visible"
          >
            <TableContainer>
              <Table variant="simple" size="sm">
                <Thead bg="gray.50">
                  <Tr>
                    <Th
                      fontSize="0.875rem"
                      fontWeight="600"
                      color="gray.800"
                    >
                      Specialty
                    </Th>
                    <Th
                      fontSize="0.875rem"
                      fontWeight="600"
                      color="gray.800"
                      borderLeft="1px solid"
                      borderColor="gray.200"
                    >
                      Schedule
                    </Th>
                    <Th
                      fontSize="0.875rem"
                      fontWeight="600"
                      color="gray.800"
                      borderLeft="1px solid"
                      borderColor="gray.200"
                    >
                      Reschedule
                    </Th>
                    <Th
                      fontSize="0.875rem"
                      fontWeight="600"
                      color="gray.800"
                      borderLeft="1px solid"
                      borderColor="gray.200"
                    >
                      Confirm
                    </Th>
                    <Th
                      fontSize="0.875rem"
                      fontWeight="600"
                      color="gray.800"
                      borderLeft="1px solid"
                      borderColor="gray.200"
                    >
                      Cancel
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {configuredSpecialties.map((specialty) => {
                    const outcome = normalizeSpecialtyOutcome(
                      transferOutcomes.specialties?.[specialty]
                    );
                    return (
                      <Tr
                        key={specialty}
                        borderBottom="1px solid"
                        borderColor="gray.200"
                      >
                        <Td fontSize="0.875rem" color="gray.800">
                          <Flex
                            justify="space-between"
                            align="center"
                            gap={2}
                          >
                            <Text fontSize="0.875rem">
                              {specialty}
                            </Text>
                            <Tooltip
                              label="Remove this specialty from transfer outcomes"
                              hasArrow
                              placement="top"
                            >
                              <Button
                                variant="link"
                                colorScheme="red"
                                size="xs"
                                onClick={() =>
                                  handleRemoveSpecialty(specialty)
                                }
                              >
                                Remove
                              </Button>
                            </Tooltip>
                          </Flex>
                        </Td>
                        {(
                          [
                            'schedule',
                            'reschedule',
                            'confirm',
                            'cancel',
                          ] as IntentType[]
                        ).map((intent) => (
                          <Td
                            key={intent}
                            borderLeft="1px solid"
                            borderColor="gray.200"
                          >
                            <RadioGroup
                              value={outcome[intent]}
                              onChange={(value) =>
                                handleSpecialtyChange(
                                  specialty,
                                  intent,
                                  value as TransferOutcome
                                )
                              }
                            >
                              <HStack spacing={4}>
                                <Radio value="transfer" size="sm">
                                  <Text fontSize="0.875rem">
                                    Transfer
                                  </Text>
                                </Radio>
                                <Radio value="not-transfer" size="sm">
                                  <Text fontSize="0.875rem">
                                    Not Transfer
                                  </Text>
                                </Radio>
                              </HStack>
                            </RadioGroup>
                          </Td>
                        ))}
                      </Tr>
                    );
                  })}
                  <Tr>
                    <Td
                      colSpan={5}
                      borderTop="1px solid"
                      borderColor="gray.200"
                    >
                      <Select
                        value={selectedSpecialty}
                        onChange={(option) =>
                          setSelectedSpecialty(
                            option as {
                              label: string;
                              value: string;
                            } | null
                          )
                        }
                        options={availableSpecialties}
                        placeholder="Search and select Specialty to add"
                        isSearchable={true}
                        isClearable={false}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        noOptionsMessage={() =>
                          availableSpecialties.length === 0
                            ? 'No specialties available'
                            : 'No matching specialties'
                        }
                        styles={{
                          control: (base, state) => ({
                            ...base,
                            minHeight: '2rem',
                            height: '2rem',
                            fontSize: '0.875rem',
                            border: state.isFocused
                              ? '1px solid #3182ce'
                              : '1px solid #e2e8f0',
                            borderRadius: '0.375rem',
                            boxShadow: 'none',
                            cursor: 'pointer',
                            '&:hover': {
                              borderColor: '#3182ce',
                            },
                          }),
                          placeholder: (base) => ({
                            ...base,
                            color: '#718096',
                            fontSize: '0.875rem',
                          }),
                          option: (base, state) => ({
                            ...base,
                            fontSize: '0.875rem',
                            backgroundColor: state.isFocused
                              ? '#ebf8ff'
                              : state.isSelected
                              ? '#3182ce'
                              : 'white',
                            color: state.isSelected
                              ? 'white'
                              : '#2d3748',
                            padding: '0.5rem 0.75rem',
                            cursor: 'pointer',
                          }),
                          menuPortal: (base) => ({
                            ...base,
                            zIndex: 9999,
                          }),
                          menu: (base) => ({
                            ...base,
                            zIndex: 9999,
                            borderRadius: '0.375rem',
                            boxShadow:
                              '0px 4px 10px rgba(0, 0, 0, 0.1)',
                          }),
                          input: (base) => ({
                            ...base,
                            fontSize: '0.875rem',
                            margin: 0,
                            padding: 0,
                          }),
                          indicatorsContainer: (base) => ({
                            ...base,
                            height: '1.75rem',
                          }),
                          indicatorSeparator: () => ({
                            display: 'none',
                          }),
                        }}
                      />
                      {selectedSpecialty && (
                        <Button
                          size="sm"
                          mt={2}
                          onClick={handleAddSpecialty}
                          colorScheme="blue"
                        >
                          Add Specialty
                        </Button>
                      )}
                    </Td>
                  </Tr>
                </Tbody>
              </Table>
            </TableContainer>
          </Box>
        </Box>
      )}

      {/* Appointment Type Outcomes */}
      <Box>
        <Text
          mb="0.5rem"
          fontSize="0.875rem"
          fontWeight="500"
          color="gray.700"
        >
          Outcome for Appointment Types:
        </Text>
        <Box
          border="1px solid"
          borderColor="gray.200"
          borderRadius="md"
          overflow="visible"
        >
          <TableContainer>
            <Table variant="simple" size="sm">
              <Thead bg="gray.50">
                <Tr>
                  <Th
                    fontSize="0.875rem"
                    fontWeight="600"
                    color="gray.800"
                  >
                    Appointment Type
                  </Th>
                  <Th
                    fontSize="0.875rem"
                    fontWeight="600"
                    color="gray.800"
                    borderLeft="1px solid"
                    borderColor="gray.200"
                  >
                    Schedule
                  </Th>
                  <Th
                    fontSize="0.875rem"
                    fontWeight="600"
                    color="gray.800"
                    borderLeft="1px solid"
                    borderColor="gray.200"
                  >
                    Reschedule
                  </Th>
                  <Th
                    fontSize="0.875rem"
                    fontWeight="600"
                    color="gray.800"
                    borderLeft="1px solid"
                    borderColor="gray.200"
                  >
                    Confirm
                  </Th>
                  <Th
                    fontSize="0.875rem"
                    fontWeight="600"
                    color="gray.800"
                    borderLeft="1px solid"
                    borderColor="gray.200"
                  >
                    Cancel
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                {configuredAppointmentTypes.map((appointmentType) => {
                  const outcome = normalizeAppointmentTypeOutcome(
                    transferOutcomes.appointmentTypes?.[
                      appointmentType
                    ]
                  );
                  return (
                    <Tr
                      key={appointmentType}
                      borderBottom="1px solid"
                      borderColor="gray.200"
                    >
                      <Td fontSize="0.875rem" color="gray.800">
                        <Flex
                          justify="space-between"
                          align="center"
                          gap={2}
                        >
                          <Text fontSize="0.875rem">
                            {getAppointmentTypeDisplayName(
                              appointmentType
                            )}
                          </Text>
                          <Tooltip
                            label="Remove this appointment type from transfer outcomes"
                            hasArrow
                            placement="top"
                          >
                            <Button
                              variant="link"
                              colorScheme="red"
                              size="xs"
                              onClick={() =>
                                handleRemoveAppointmentType(
                                  appointmentType
                                )
                              }
                            >
                              Remove
                            </Button>
                          </Tooltip>
                        </Flex>
                      </Td>
                      {(
                        [
                          'schedule',
                          'reschedule',
                          'confirm',
                          'cancel',
                        ] as IntentType[]
                      ).map((intent) => (
                        <Td
                          key={intent}
                          borderLeft="1px solid"
                          borderColor="gray.200"
                        >
                          <RadioGroup
                            value={outcome[intent]}
                            onChange={(value) =>
                              handleAppointmentTypeChange(
                                appointmentType,
                                intent,
                                value as TransferOutcome
                              )
                            }
                          >
                            <HStack spacing={4}>
                              <Radio value="transfer" size="sm">
                                <Text fontSize="0.875rem">
                                  Transfer
                                </Text>
                              </Radio>
                              <Radio value="not-transfer" size="sm">
                                <Text fontSize="0.875rem">
                                  Not Transfer
                                </Text>
                              </Radio>
                            </HStack>
                          </RadioGroup>
                        </Td>
                      ))}
                    </Tr>
                  );
                })}
                <Tr>
                  <Td
                    colSpan={5}
                    borderTop="1px solid"
                    borderColor="gray.200"
                  >
                    <Select
                      value={selectedAppointmentType}
                      onChange={(option) =>
                        setSelectedAppointmentType(
                          option as {
                            label: string;
                            value: string;
                          } | null
                        )
                      }
                      options={availableAppointmentTypes}
                      placeholder="Search and select appointment type to add..."
                      isSearchable={true}
                      isClearable={false}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      styles={{
                        control: (base, state) => ({
                          ...base,
                          minHeight: '2rem',
                          height: '2rem',
                          fontSize: '0.875rem',
                          border: state.isFocused
                            ? '1px solid #3182ce'
                            : '1px solid #e2e8f0',
                          borderRadius: '0.375rem',
                          boxShadow: 'none',
                          cursor: 'pointer',
                          '&:hover': {
                            borderColor: '#3182ce',
                          },
                        }),
                        placeholder: (base) => ({
                          ...base,
                          color: '#718096',
                          fontSize: '0.875rem',
                        }),
                        option: (base, state) => ({
                          ...base,
                          fontSize: '0.875rem',
                          backgroundColor: state.isFocused
                            ? '#ebf8ff'
                            : state.isSelected
                            ? '#3182ce'
                            : 'white',
                          color: state.isSelected
                            ? 'white'
                            : '#2d3748',
                          padding: '0.5rem 0.75rem',
                          cursor: 'pointer',
                        }),
                        menuPortal: (base) => ({
                          ...base,
                          zIndex: 9999,
                        }),
                        menu: (base) => ({
                          ...base,
                          zIndex: 9999,
                          borderRadius: '0.375rem',
                          boxShadow:
                            '0px 4px 10px rgba(0, 0, 0, 0.1)',
                        }),
                        input: (base) => ({
                          ...base,
                          fontSize: '0.875rem',
                          margin: 0,
                          padding: 0,
                        }),
                        indicatorsContainer: (base) => ({
                          ...base,
                          height: '1.75rem',
                        }),
                        indicatorSeparator: () => ({
                          display: 'none',
                        }),
                      }}
                    />
                    {selectedAppointmentType && (
                      <Button
                        size="sm"
                        mt={2}
                        onClick={handleAddAppointmentType}
                        colorScheme="blue"
                      >
                        Add Appointment Type
                      </Button>
                    )}
                  </Td>
                </Tr>
              </Tbody>
            </Table>
          </TableContainer>
        </Box>
      </Box>

      {/* Comment Log (Audit Trail) */}
      {commentLog.length > 0 && (
        <Box>
          <Text
            mb="1rem"
            fontSize="0.9375rem"
            fontWeight="600"
            color="gray.800"
          >
            Comment Log
          </Text>
          <VStack
            align="stretch"
            spacing={3}
            maxH="20rem"
            overflowY="auto"
            css={{
              '&::-webkit-scrollbar': {
                width: '6px',
              },
              '&::-webkit-scrollbar-track': {
                background: '#f1f1f1',
                borderRadius: '10px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#cbd5e0',
                borderRadius: '10px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: '#a0aec0',
              },
            }}
          >
            {commentLog.map((entry, index) => (
              <Box
                key={index}
                p="1rem"
                border="1px solid"
                borderColor="gray.200"
                borderRadius="lg"
                bg="white"
                boxShadow="sm"
                _hover={{
                  boxShadow: 'md',
                  borderColor: 'gray.300',
                }}
                transition="all 0.2s"
              >
                <Flex align="flex-start" gap={3}>
                  {/* Avatar with Popover */}
                  <Popover trigger="click" placement="right">
                    <PopoverTrigger>
                      <Avatar
                        size="sm"
                        name={
                          entry.userName ||
                          entry.userEmail ||
                          entry.userId ||
                          'User'
                        }
                        bg={`${getActionBadgeColor(
                          entry.action
                        )}.500`}
                        color="white"
                        cursor="pointer"
                        _hover={{
                          transform: 'scale(1.1)',
                        }}
                        transition="transform 0.2s"
                        getInitials={() => getEntryInitials(entry)}
                      />
                    </PopoverTrigger>
                    <PopoverContent
                      w="auto"
                      minW="200px"
                      fontSize="0.875rem"
                    >
                      <PopoverBody>
                        <VStack align="stretch" spacing={2}>
                          <Box>
                            <Text
                              fontSize="0.75rem"
                              color="gray.500"
                              mb="0.25rem"
                            >
                              Name
                            </Text>
                            <Text fontWeight="600" color="gray.800">
                              {entry.userName || 'Not available'}
                            </Text>
                          </Box>
                          <Box>
                            <Text
                              fontSize="0.75rem"
                              color="gray.500"
                              mb="0.25rem"
                            >
                              Email
                            </Text>
                            <Text color="gray.700">
                              {entry.userEmail || 'Not available'}
                            </Text>
                          </Box>
                          <Box>
                            <Text
                              fontSize="0.75rem"
                              color="gray.500"
                              mb="0.25rem"
                            >
                              User ID
                            </Text>
                            <Text
                              fontSize="0.75rem"
                              color="gray.600"
                              fontFamily="mono"
                            >
                              {entry.userId}
                            </Text>
                          </Box>
                          {entry.permissionGroup && (
                            <Box>
                              <Text
                                fontSize="0.75rem"
                                color="gray.500"
                                mb="0.25rem"
                              >
                                Permission Group
                              </Text>
                              <Badge
                                colorScheme="blue"
                                variant="subtle"
                                fontSize="0.75rem"
                              >
                                {entry.permissionGroup}
                              </Badge>
                            </Box>
                          )}
                        </VStack>
                      </PopoverBody>
                    </PopoverContent>
                  </Popover>

                  {/* Content */}
                  <Box flex={1} minW={0}>
                    <Flex
                      align="center"
                      gap={2}
                      mb="0.5rem"
                      flexWrap="wrap"
                    >
                      <Badge
                        colorScheme={getActionBadgeColor(
                          entry.action
                        )}
                        variant="subtle"
                        fontSize="0.6875rem"
                        fontWeight="600"
                        px={2}
                        py={0.5}
                        borderRadius="md"
                      >
                        {getActionText(entry.action)}
                      </Badge>
                      <Text
                        fontSize="0.8125rem"
                        color="gray.700"
                        fontWeight="500"
                      >
                        {getUserDisplayName(entry)}
                      </Text>
                      <Text
                        fontSize="0.75rem"
                        color="gray.500"
                        ml="auto"
                        whiteSpace="nowrap"
                      >
                        {formatTimestamp(entry.timestamp)}
                      </Text>
                    </Flex>
                    {entry.comment && (
                      <Box
                        mt="0.75rem"
                        p="0.75rem"
                        bg="gray.50"
                        borderRadius="md"
                        borderLeft="3px solid"
                        borderLeftColor={`${getActionBadgeColor(
                          entry.action
                        )}.400`}
                      >
                        <FormattedText content={entry.comment} />
                      </Box>
                    )}
                  </Box>
                </Flex>
              </Box>
            ))}
          </VStack>
        </Box>
      )}

      {/* Divider between log and comment input */}
      {commentLog.length > 0 && <Divider my={4} />}

      {/* Comment Input */}
      <Box>
        <Text
          mb="0.5rem"
          fontSize="0.875rem"
          fontWeight="500"
          color="gray.700"
        >
          Comment
        </Text>
        <Text fontSize="0.75rem" color="gray.600" mb="0.5rem">
          Add a comment about this change. Rich text formatting is
          supported.
        </Text>
        <RichTextEditor
          value={comment}
          onChange={onCommentChange}
          placeholder="Enter comment..."
          minHeight="4rem"
          maxHeight="20rem"
        />
        {onAddComment && hasExistingId && (
          <Tooltip
            label={getAddCommentTooltip()}
            hasArrow
            placement="top"
            isDisabled={Boolean(comment.trim()) && !isAddingComment}
          >
            <Box display="inline-block">
              <Button
                mt="0.75rem"
                onClick={onAddComment}
                disabled={!comment.trim() || isAddingComment}
                isLoading={isAddingComment}
                colorScheme="blue"
                size="sm"
                variant="outline"
              >
                Add Comment
              </Button>
            </Box>
          </Tooltip>
        )}
        {!hasExistingId && (
          <Text
            fontSize="0.75rem"
            color="orange.600"
            mt="0.5rem"
            fontStyle="italic"
          >
            Note: Save the alert or note first to enable adding
            comments.
          </Text>
        )}
      </Box>

      {/* Action Buttons */}
      {hasExistingId &&
        (showSubmitForApproval || showApproveButton) && (
          <Flex
            justify="space-between"
            gap={4}
            align="flex-start"
            flexWrap="wrap"
          >
            {showSubmitForApproval && (
              <Box>
                <Tooltip
                  label={getSubmitForApprovalTooltip()}
                  hasArrow
                  placement="top"
                  isDisabled={canSubmitForApproval && !isSubmitting}
                >
                  <Box display="inline-block">
                    <Button
                      onClick={onSubmitForApproval}
                      disabled={!canSubmitForApproval || isSubmitting}
                      isLoading={isSubmitting}
                      colorScheme="blue"
                      size="md"
                      minW="12rem"
                    >
                      Submit for Approval
                    </Button>
                  </Box>
                </Tooltip>
                {!comment.trim() && (
                  <Text
                    fontSize="0.75rem"
                    color="orange.600"
                    mt="0.5rem"
                    fontStyle="italic"
                  >
                    Warning: Add a comment to enable submission.
                  </Text>
                )}
              </Box>
            )}
            {showApproveButton && (
              <Box>
                <Tooltip
                  label={(() => {
                    if (!canApprove) {
                      return (
                        approveDisabledReason ||
                        'Submit for approval before approving transfer outcomes'
                      );
                    }
                    return isApproving
                      ? 'Approving transfer outcomes...'
                      : 'Approve transfer outcomes to make them live';
                  })()}
                  hasArrow
                  placement="top"
                >
                  <Box display="inline-block">
                    <Button
                      onClick={() => {
                        if (onApprove) {
                          onApprove();
                        } else {
                          console.error(
                            'onApprove handler is not provided'
                          );
                        }
                      }}
                      disabled={isApproving || !canApprove}
                      isLoading={isApproving}
                      colorScheme="green"
                      size="md"
                      minW="12rem"
                      ml="auto"
                    >
                      Approve Transfer Outcomes
                    </Button>
                  </Box>
                </Tooltip>
                {!canApprove && (
                  <Text
                    fontSize="0.75rem"
                    color="orange.600"
                    mt="0.5rem"
                    fontStyle="italic"
                  >
                    {approveDisabledReason ||
                      'Submit for approval before CHS can approve.'}
                  </Text>
                )}
              </Box>
            )}
          </Flex>
        )}
    </VStack>
  );
};

export default TransferOutcomesConfig;
